// The one place a failed settlement action becomes a toast.
//
// Every surface that takes an action against the roster — the reassign dialog,
// the feed rows, the item drawer — reports its failures through here, so the
// admin reads one banner per failure and always reads DESIGNED copy rather than
// whatever sentence the API happened to send. Two rules, both enforced here
// rather than remembered at each call site:
//
//  1. Copy comes from `error-copy.ts`, keyed on the API error code. Response
//     text is not an input, so it cannot leak into a surface.
//  2. A failure the HTTP interceptor already toasted (401/403 — the session or
//     the role) is not toasted again. That pairing is where the duplicate came
//     from: the interceptor said "Permission denied" and the action said
//     "Could not reassign the lot" about the same rejected request.
// /* §edge 5 what a failed action says */
import { useToast } from '@/shell';

import { settlementErrorCopy, type ActionFailureCopy } from './error-copy';
import { useSettlementStore } from './store';

/** How long a failure banner stays up. Long enough to read a two-line sentence. */
const FAILURE_TOAST_LIFE_MS = 8000;

export interface SettlementActionToast {
  /**
   * Report the store's last failed action.
   *
   * `fallback` names the action for the unmapped-code case ("Could not reassign
   * the lot"); pass nothing to use the generic line. `detailSuffix` appends the
   * subject the admin was acting on (a lot title, an invoice number) so the
   * banner says WHICH one.
   *
   * Returns whether a toast was emitted — false means a layer above already
   * told the admin about this exact failure.
   */
  reportFailure: (fallback?: ActionFailureCopy, subject?: string) => boolean;
}

export function useSettlementActionToast(): SettlementActionToast {
  const store = useSettlementStore();
  const toast = useToast();

  function reportFailure(fallback?: ActionFailureCopy, subject?: string): boolean {
    const failure = store.lastActionError;
    if (failure?.reported) return false;

    const copy = settlementErrorCopy(failure?.code ?? null, fallback);
    toast.danger({
      summary: copy.summary,
      detail: subject ? `${subject}. ${copy.detail}` : copy.detail,
      life: FAILURE_TOAST_LIFE_MS,
    });
    return true;
  }

  return { reportFailure };
}
